import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ShareIcon from "@mui/icons-material/Share";
import { useState } from "react";

import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Divider,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField as MuiTextField,
  Popper,
  Fade,
  Paper,
} from "@mui/material";
import Toast from './Toast';

export default function RegistrationLinks({ shortCode }) {
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });

  const [openEmailDialog, setOpenEmailDialog] = useState(false);
  const [emailData, setEmailData] = useState({ name: "", email: "" });
  const [currentUrl, setCurrentUrl] = useState("")
  const [anchorEl, setAnchorEl] = useState(null);

  // State for the copy popup
  const [copyAnchorEl, setCopyAnchorEl] = useState(null);
  const [copyOpen, setCopyOpen] = useState(false);

  const registrationLink = window.location.origin + "/appuserregistration/" + shortCode;
  const androidLink = "https://play.google.com/store/apps/details?id=com.glocalprayer.ep&pcampaignid=web_share";
  const iosLink = "https://apps.apple.com/in/app/glocal-prayer-network/id6479648726";


  // ✅ Copy to clipboard
  const handleCopy = async (event, type) => {
    // Set anchor for the popup immediately
    setCopyAnchorEl(event.currentTarget);

    let url = "";
    if (type == "registration") {
      url = registrationLink;
    }
    if (type == "android") {
      url = androidLink;
    }
    if (type == "ios") {
      url = iosLink;
    }

    const showSuccess = () => {
      setCopyOpen(true);
      setTimeout(() => {
        setCopyOpen(false);
        setCopyAnchorEl(null);
      }, 1000);
    };

    if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
      // Modern method (requires HTTPS or localhost)
      return navigator.clipboard.writeText(url)
        .then(() => {
          showSuccess();
          console.log("Copied using Clipboard API");
        })
        .catch((err) => {
          console.error("Clipboard API failed:", err);
        });
    } else {
      // Fallback for HTTP / older browsers
      const textarea = document.createElement("textarea");
      textarea.value = url;
      textarea.style.position = "fixed";  // avoid scrolling to bottom
      textarea.style.opacity = "0";       // invisible
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();

      try {
        document.execCommand("copy");
        showSuccess();
        console.log("Copied using execCommand fallback");
      } catch (err) {
        console.error("Fallback copy failed:", err);
      }

      document.body.removeChild(textarea);
    }
  };

  // ✅ Share menu
  const handleShareClick = (event, link) => {
    setAnchorEl(event.currentTarget); // this is the DOM element to anchor the menu
    setCurrentUrl(link);              // this is your actual link
  };
  const handleCloseMenu = () => setAnchorEl(null);

  // ✅ WhatsApp share
  const handleWhatsAppShare = () => {
    window.open(
      `https://wa.me/?text=${encodeURIComponent(currentUrl)}`,
      "_blank"
    );
    handleCloseMenu();
  };

  // ✅ Email modal
  const handleEmailShare = () => {
    setOpenEmailDialog(true);
    handleCloseMenu();
  };

  const handleEmailSend = () => {
    const { name, email } = emailData;
    if (!name || !email) {
      setToast({ open: true, message: "Please enter name and email", severity: "error" });
      return;
    }
    // Using mailto for demo (you can replace with API call)
    window.location.href = `mailto:${email}?subject=Registration Link&body=Hi ${name},\n\nHere is your registration link: ${currentUrl}`;
    setOpenEmailDialog(false);
    setEmailData({ name: "", email: "" });
  };

  return (
    <Card elevation={0} sx={{
      width: "100%",
      maxWidth: "100%",
      overflowX: "hidden",
      p: { xs: 2, sm: 4 },
      borderRadius: 2,
      background: 'rgba(255,255,255,0.05)',
      backdropFilter: 'blur(10px)',
      border: '1px solid rgba(255,255,255,0.1)',
    }}>
      <Typography variant="h6" sx={{
        fontWeight: 700,
        color: '#FFFFFF',
        mb: 3,
        fontSize: '1.125rem',
      }}>
        Important Links
      </Typography>
      <CardContent sx={{ p: 0 }}>
        {/* User Registration */}
        <Typography variant="subtitle2" sx={{ color: '#4CA1AF', fontWeight: 600, mb: 1 }}>
          User Registration
        </Typography>
        <Typography variant="h6" sx={{ fontWeight: 600, color: '#FFFFFF', mb: 1 }}>
          Web
        </Typography>

        <TextField
          fullWidth
          margin="normal"
          value={registrationLink}
          InputProps={{
            readOnly: true,
            sx: {
              color: '#FFFFFF',
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(255,255,255,0.2)',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(255,255,255,0.3)',
              },
            },
            endAdornment: (
              <InputAdornment position="end">
                <IconButton sx={{ color: '#10B981' }} onClick={(e) => handleCopy(e, "registration")}>
                  <ContentCopyIcon />
                </IconButton>
                <IconButton sx={{ color: '#4CA1AF' }} onClick={(event) => handleShareClick(event, registrationLink)}>
                  <ShareIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        {/* Share Menu */}
        <Menu anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleCloseMenu}>
          <MenuItem onClick={handleWhatsAppShare}>WhatsApp</MenuItem>
          <MenuItem onClick={handleEmailShare}>Email</MenuItem>
        </Menu>

        <Typography variant="body2" sx={{ mt: 1, mb: 2, color: 'rgba(255,255,255,0.7)' }}>
          <strong>Note:</strong> Users can use this link to become a member of the organization.
        </Typography>

        <Divider sx={{ my: 2, borderColor: 'rgba(255,255,255,0.1)' }} />

        {/* Mobile Apps */}
        <Typography variant="subtitle2" sx={{ color: '#4CA1AF', fontWeight: 600, mb: 2 }}>
          Mobile Apps
        </Typography>

        <Box display="flex" gap={2} flexWrap="wrap" sx={{ width: '100%' }}>
          {/* Android */}
          <Box flex={1} sx={{ minWidth: { xs: '100%', sm: 200 } }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#FFFFFF', mb: 1 }}>
              Android
            </Typography>
            <TextField
              fullWidth
              margin="normal"
              value={androidLink}
              InputProps={{
                readOnly: true,
                sx: {
                  color: '#FFFFFF',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255,255,255,0.2)',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255,255,255,0.3)',
                  },
                },
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton sx={{ color: '#10B981' }} onClick={(e) => handleCopy(e, "android")}>
                      <ContentCopyIcon />
                    </IconButton>
                    <IconButton sx={{ color: '#4CA1AF' }} onClick={(event) => handleShareClick(event, androidLink)}>
                      <ShareIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {/* iOS */}
          <Box flex={1} sx={{ minWidth: { xs: '100%', sm: 200 } }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#FFFFFF', mb: 1 }}>
              iOS
            </Typography>
            <TextField
              fullWidth
              margin="normal"
              value={iosLink}
              InputProps={{
                readOnly: true,
                sx: {
                  color: '#FFFFFF',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255,255,255,0.2)',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255,255,255,0.3)',
                  },
                },
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton sx={{ color: '#10B981' }} onClick={(e) => handleCopy(e, "ios")}>
                      <ContentCopyIcon />
                    </IconButton>
                    <IconButton sx={{ color: '#4CA1AF' }} onClick={(event) => handleShareClick(event, iosLink)}>
                      <ShareIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        </Box>

        <Typography variant="body2" mt={1}>
          <strong>Note:</strong> Members can use these links to download the App for Android or iOS.
        </Typography>
      </CardContent>

      {/* Email Dialog */}
      <Dialog open={openEmailDialog} onClose={() => setOpenEmailDialog(false)}>
        <DialogTitle>Send Registration Link via Email</DialogTitle>
        <DialogContent>
          <MuiTextField
            margin="dense"
            label="Name"
            fullWidth
            value={emailData.name}
            onChange={(e) => setEmailData({ ...emailData, name: e.target.value })}
          />
          <MuiTextField
            margin="dense"
            label="Email"
            type="email"
            fullWidth
            value={emailData.email}
            onChange={(e) => setEmailData({ ...emailData, email: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEmailDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleEmailSend}>
            Send
          </Button>
        </DialogActions>
      </Dialog>

      {/* Copied Message Popper */}
      <Popper
        open={copyOpen}
        anchorEl={copyAnchorEl}
        placement="bottom"
        transition
        sx={{ zIndex: 1500 }}
      >
        {({ TransitionProps }) => (
          <Fade {...TransitionProps} timeout={350}>
            <Paper sx={{
              p: 1,
              px: 2,
              mt: 1,
              background: '#10B981',
              color: '#FFFFFF',
              borderRadius: 1,
              boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
            }}>
              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                Copied!
              </Typography>
            </Paper>
          </Fade>
        )}
      </Popper>
      <Toast toast={toast} setToast={setToast} />
    </Card >
  );
}
